/**
 * scenarios.ts — küratörlü tartışma şablonları.
 *
 * Wizard'ın "Senaryo Kütüphanesi" modal'ı tarafından tüketilir. Her senaryo
 * tek tıkla wizard state'ine query + topology + agentCount yazar.
 *
 * Tasarım kuralları:
 *   • Senaryolar GENEL kurumsal kitleye hitap eder (savunma sanayii, yazılım,
 *     finans, sağlık, perakende karışımı). Spesifik bir sektöre kilitlenmez.
 *   • Her query 200-700 karakter — ne çok kısa (yüzeysel) ne çok uzun
 *     (LLM zaten konuya hâkim olduğu için uzun queries fark yaratmaz).
 *   • topology / agentCount önerileri "best fit" ipucudur; kullanıcı yine de
 *     wizard'dan değiştirebilir.
 */
import type { DebateTopology } from "../../domain/types";

export type ScenarioCategoryId =
  | "strategy"
  | "tech"
  | "ethics"
  | "org"
  | "risk"
  | "sustainability"
  | "geopolitics";

export interface ScenarioCategory {
  id: ScenarioCategoryId;
  label: string;
  /** Tek satırlık ipucu — modal sol panelde liste altında gösterilir. */
  hint: string;
  /** Lucide icon name — modal tarafında dinamik resolve edilir. */
  icon?: string;
}

export interface Scenario {
  id: string;
  title: string;
  category: ScenarioCategoryId;
  query: string;
  recommendedTopology?: DebateTopology;
  recommendedAgents?: number;
  estimatedMinutes?: number;
}

export const SCENARIO_CATEGORIES: readonly ScenarioCategory[] = [
  { id: "strategy",       label: "Strateji & Büyüme",     hint: "Pazar, ürün ve büyüme kararları" },
  { id: "tech",           label: "Teknoloji & Mimari",    hint: "Sistem tasarımı, yığın seçimi" },
  { id: "ethics",         label: "Etik & Yönetişim",      hint: "AI, veri ve kurumsal yönetişim" },
  { id: "org",            label: "Organizasyon & Kültür", hint: "Yapı, hiyerarşi ve verimlilik" },
  { id: "risk",           label: "Risk & Uyumluluk",      hint: "Regülasyon, güvenlik, kriz" },
  { id: "sustainability", label: "Sürdürülebilirlik",     hint: "ESG, döngüsel ekonomi" },
  { id: "geopolitics",    label: "Jeopolitik & Pazarlar", hint: "Küresel dinamikler, ticaret" },
] as const;

export const SCENARIOS: readonly Scenario[] = [
  // ---------- Strategy & Growth ----------
  {
    id: "strategy-pricing-overhaul",
    title: "Fiyatlandırma Modeli Yeniden Tasarımı",
    category: "strategy",
    query:
      "Yıllık abonelik ve kullanım-bazlı (PAYG) fiyatlandırma arasında hibrit bir model kurmak istiyoruz. Müşteri segmentasyonuna göre farklı paketler, kurumsal indirim eşikleri ve enterprise tier nasıl konumlandırılmalı? Müşteri kaybı (churn) riskini minimize ederken brüt kâr marjını koruyacak tasarımı tartışın.",
    recommendedTopology: "diverse-poles",
    recommendedAgents: 6,
    estimatedMinutes: 12,
  },
  {
    id: "strategy-market-entry-eu",
    title: "AB Pazarına Giriş Stratejisi",
    category: "strategy",
    query:
      "Türkiye merkezli bir SaaS şirketi olarak Almanya, Hollanda ve İskandinavya pazarına giriş yapmak istiyoruz. Yerel ofis vs. uzaktan satış, yerelleştirme önceliği, GDPR uyumluluğu, partner ekosistem inşası ve marka bilinirliği yatırımı için 18 aylık yol haritası tartışın.",
    recommendedTopology: "round-robin",
    recommendedAgents: 7,
    estimatedMinutes: 14,
  },
  {
    id: "strategy-build-vs-buy",
    title: "Yap mı Satın Al mı: Veri Platformu",
    category: "strategy",
    query:
      "İç veri platformumuzu sıfırdan inşa etmek (Snowflake + dbt + Airflow) ile hazır bir kurumsal çözümü (Databricks veya Palantir Foundry) satın almak arasında karar vermeliyiz. 3 yıllık TCO, vendor lock-in riski, in-house yetenek geliştirme ve hız-pazara değerlendirin.",
    recommendedTopology: "cross-fire",
    recommendedAgents: 5,
    estimatedMinutes: 10,
  },

  // ---------- Tech & Architecture ----------
  {
    id: "tech-monolith-to-services",
    title: "Monolitten Servis Mimarisine Göç",
    category: "tech",
    query:
      "10 yıllık bir Django monolitini servis tabanlı (mikroservis veya modüler monolit) bir mimariye nasıl taşımalıyız? Strangler Fig pattern, event-driven entegrasyon (Kafka), kademeli kesim stratejisi ve veri tutarlılığı modellerini (saga, CDC) tartışın. Riskler ve geri dönüş planı?",
    recommendedTopology: "round-robin",
    recommendedAgents: 6,
    estimatedMinutes: 12,
  },
  {
    id: "tech-llm-infra",
    title: "Üretim Ortamında LLM Servis Mimarisi",
    category: "tech",
    query:
      "Kurumsal müşterilerimize sunduğumuz AI özellikleri için kendi LLM servisimizi (vLLM + Llama-3 70B) mi koşmalıyız yoksa yönetilen API'lere (OpenAI, Anthropic) mi yaslanmalıyız? Latans, maliyet, gizlilik (KVKK / GDPR), hata payı (hallucination), failover ve gelecek-uyumluluk dengesi.",
    recommendedTopology: "diverse-poles",
    recommendedAgents: 7,
    estimatedMinutes: 13,
  },
  {
    id: "tech-multi-region-failover",
    title: "Çoklu Bölge Felaket Kurtarma",
    category: "tech",
    query:
      "Tek bölgede çalışan bir e-ticaret platformunu çoklu-bölge aktif-aktif mimariye taşımak istiyoruz. RPO < 5 dakika, RTO < 15 dakika hedefiyle veritabanı replikasyon stratejisi (Aurora Global vs. CockroachDB), DNS failover, edge caching ve maliyet etkisini tartışın.",
    recommendedTopology: "moderator-led",
    recommendedAgents: 5,
    estimatedMinutes: 11,
  },

  // ---------- Ethics & Governance ----------
  {
    id: "ethics-ai-hiring",
    title: "İK Süreçlerinde AI Kullanımı",
    category: "ethics",
    query:
      "Aday özgeçmişlerini ön elemeden geçiren bir AI sisteminin kurulması düşünülüyor. Algoritmik önyargı, açıklanabilirlik, KVKK ve AB AI Act uyumluluğu, insan-üstünde-döngü (HITL) gerekleri ve şirketin sosyal sorumluluğu açısından etik çerçeve tartışın.",
    recommendedTopology: "diverse-poles",
    recommendedAgents: 7,
    estimatedMinutes: 13,
  },
  {
    id: "ethics-data-monetization",
    title: "Müşteri Verisi Para Kazanma Modeli",
    category: "ethics",
    query:
      "Anonim hale getirilmiş müşteri davranış verisini üçüncü partilerle paylaşıp gelir elde etmeyi düşünüyoruz. Yasal çerçeve (GDPR / KVKK), itibar riski, müşteri güveni, alternatif gelir modelleri ve etik kırmızı çizgileri tartışın.",
    recommendedTopology: "cross-fire",
    recommendedAgents: 5,
    estimatedMinutes: 10,
  },

  // ---------- Organization & Culture ----------
  {
    id: "org-hybrid-work",
    title: "Tam Uzaktan vs. Hibrit Çalışma",
    category: "org",
    query:
      "300 kişilik bir teknoloji şirketi olarak tam uzaktan, 2 gün ofis hibrit veya 4 gün ofis modelleri arasında karar vermeliyiz. Çalışan tatmini, üretkenlik, ofis maliyeti, organizasyonel kültür sürdürülebilirliği, junior çalışan yetiştirme ve yetenek havuzu erişimi açısından değerlendirin.",
    recommendedTopology: "round-robin",
    recommendedAgents: 6,
    estimatedMinutes: 11,
  },
  {
    id: "org-flatten-hierarchy",
    title: "Yönetim Katmanlarını Azaltma",
    category: "org",
    query:
      "Mevcut 7 yönetim katmanından 4'e düşmeyi planlıyoruz. Karar hızı, raporlama yapısı, orta-seviye liderlik motivasyonu, yetki devri (delegation) çerçevesi ve tek-noktada-arıza riski tartışın. Geçiş stratejisi ve insan kaynağı etkisi nasıl yönetilmeli?",
    recommendedTopology: "moderator-led",
    recommendedAgents: 5,
    estimatedMinutes: 10,
  },

  // ---------- Risk & Compliance ----------
  {
    id: "risk-cyber-incident",
    title: "Fidye Yazılımı Olay Müdahalesi",
    category: "risk",
    query:
      "Üretim sistemlerimizin %40'ı fidye yazılımıyla şifrelendi. Fidye ödenmeli mi, yedeklerden geri dönüş ne kadar sürer, müşterilere ve regülatöre ne zaman bildirilmeli, basın ile iletişim stratejisi nasıl olmalı? Hukuki, operasyonel, itibarsal ve etik boyutları tartışın.",
    recommendedTopology: "moderator-led",
    recommendedAgents: 7,
    estimatedMinutes: 14,
  },
  {
    id: "risk-supplier-concentration",
    title: "Tedarikçi Yoğunlaşma Riski",
    category: "risk",
    query:
      "Kritik bileşenlerin %85'ini tek bir tedarikçiden alıyoruz. Çoklu tedarikçi stratejisine geçişin maliyet etkisi, kalite kontrol karmaşıklığı, jeopolitik risk azaltımı, stok stratejisi (just-in-time vs. just-in-case) ve nearshoring/reshoring fırsatları tartışın.",
    recommendedTopology: "round-robin",
    recommendedAgents: 6,
    estimatedMinutes: 12,
  },
  {
    id: "risk-financial-stress-test",
    title: "Resesyon Senaryosu Stres Testi",
    category: "risk",
    query:
      "12 ay sürecek %3-5 daralma senaryosunda nakit akışını korumak için işten çıkarma, ürün-portföyü budama, satın alma fırsatları, sermaye-arttırımı ve maliyet azaltma seçenekleri tartışın. Hangi yatırımlardan vazgeçilmemeli, hangi maliyetler ilk kesilmeli?",
    recommendedTopology: "diverse-poles",
    recommendedAgents: 6,
    estimatedMinutes: 12,
  },
  {
    id: "risk-vendor-due-diligence",
    title: "Kurumsal Durum Tespiti: Tedarikçi Soruşturması",
    category: "risk",
    query:
      "Stratejik bir tedarikçiyle ortaklık öncesi kapsamlı bir durum tespiti (due diligence) yürütüyoruz. Şirketin mali geçmişinde usulsüzlük iddiaları, ortaklık yapısındaki kayıt dışı fon hareketleri, yöneticilerin geçmiş hukuki ihtilafları ve MASAK/regülatör kayıtlarındaki bulgular araştırılmalı. Sözleşmede adı geçen her taraf ve kilit yönetici için ayrı ayrı kanıta dayalı bir bulgu raporu çıkarın; hiçbir isim atlanmadan.",
    recommendedTopology: "investigation",
    recommendedAgents: 5,
    estimatedMinutes: 15,
  },

  // ---------- Sustainability ----------
  {
    id: "sustain-net-zero-2030",
    title: "2030 Net-Sıfır Yol Haritası",
    category: "sustainability",
    query:
      "Scope 1, 2 ve 3 emisyonlarını 2030'a kadar net-sıfıra indirme hedefi. Yenilenebilir enerji satın alımları, tedarik zinciri dönüşümü, karbon dengeleme (offset) etiği, çalışan seyahat politikaları ve raporlama (CDP, GRI, ISSB) çerçevesi tartışın. Yatırım önceliklendirmesi?",
    recommendedTopology: "round-robin",
    recommendedAgents: 7,
    estimatedMinutes: 13,
  },
  {
    id: "sustain-circular-product",
    title: "Döngüsel Ürün Tasarımı",
    category: "sustainability",
    query:
      "Donanım ürünlerimizi tek-kullanımlık modelden kiralama + geri-toplama + yenileme modeline dönüştürmek istiyoruz. Pazara değer önerisi, lojistik altyapı, modüler tasarım gerekleri, gelir modeli etkisi ve müşteri davranış değişimi tartışın.",
    recommendedTopology: "cross-fire",
    recommendedAgents: 5,
    estimatedMinutes: 10,
  },

  // ---------- Geopolitics & Markets ----------
  {
    id: "geo-china-decoupling",
    title: "Çin Tedarik Zincirinden Ayrışma",
    category: "geopolitics",
    query:
      "Üretimimizin %60'ı Çin'de. ABD-Çin gerilimi, gümrük tarifeleri ve AB karbon sınır vergisi göz önüne alındığında Vietnam, Hindistan, Meksika veya Türkiye'ye kademeli kayışın 5 yıllık planı. Maliyet, yetenek, kalite ve jeopolitik risk dengesini tartışın.",
    recommendedTopology: "diverse-poles",
    recommendedAgents: 7,
    estimatedMinutes: 13,
  },
  {
    id: "geo-emerging-currency",
    title: "Yüksek Enflasyonlu Pazarda Faaliyet",
    category: "geopolitics",
    query:
      "Yıllık enflasyonun %50+ olduğu bir pazarda faaliyet gösteriyoruz. Hedge stratejileri, USD/EUR-bazlı fiyatlandırma, çalışan ücret artışları, sermaye repatriasyonu, yerel borçlanma ve uzun-vadeli yatırım kararları tartışın.",
    recommendedTopology: "round-robin",
    recommendedAgents: 6,
    estimatedMinutes: 11,
  },
] as const;
